import {
  FieldError,
  FieldValues,
  SetValueConfig,
  SubmitHandler,
  UseFormRegisterReturn,
  UseFormReturn,
} from 'react-hook-form';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface forms<T extends FieldValues> extends Omit<
  React.FormHTMLAttributes<HTMLFormElement>,
  'onSubmit'
> {
  form: UseFormReturn<T>;
  onSubmit?: SubmitHandler<T>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const FormContext = createContext<UseFormReturn<any> | null>(null);

export function Form<T extends FieldValues>({ children, onSubmit, form, ...props }: forms<T>) {
  return (
    <FormContext.Provider value={form}>
      <form onSubmit={onSubmit ? form.handleSubmit(onSubmit) : undefined} {...props}>
        {children}
      </form>
    </FormContext.Provider>
  );
}

export interface FieldProps<T extends HTMLElement>
  extends
    Omit<React.HTMLAttributes<T>, 'onChange' | 'onBlur' | 'onFocus'>,
    Omit<UseFormRegisterReturn<string>, 'onBlur' | 'onFocus'> {
  onBlur: (e: React.FocusEvent<T>) => void;
  onFocus: (e: React.FocusEvent<T>) => void;
}

export function useField<TValue = string, T extends HTMLElement = HTMLElement>(
  name: string,
  restProps: React.HTMLAttributes<T> | undefined,
) {
  const form = useContext(FormContext);
  const [isFocused, setIsFocused] = useState(false);
  if (!form) {
    throw new Error('useField must be used within a Form component');
  }

  const formFieldProps = form.register(name);

  const handleFocus = useCallback(
    (e: React.FocusEvent<T>) => {
      setIsFocused(true);

      restProps?.onFocus?.(e);
    },
    [restProps],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<T>) => {
      setIsFocused(false);

      formFieldProps?.onBlur?.(e);
      restProps?.onBlur?.(e);
    },
    [formFieldProps, restProps],
  );

  const setValue = useCallback(
    (newValue: TValue, options?: SetValueConfig) => {
      form.setValue(name, newValue, { shouldValidate: true, ...(options ?? {}) });
    },
    [form, name],
  );

  const getValue = useCallback(() => {
    return form.watch(name);
  }, [form, name]);

  const fieldState = useMemo(
    () => ({ ...form.getFieldState(name), isFocused }),
    [form, name, isFocused],
  );

  const fieldProps: FieldProps<T> = useMemo(
    () => ({
      ...restProps,
      ...formFieldProps,
      onFocus: handleFocus,
      onBlur: handleBlur,
    }),
    [restProps, formFieldProps, handleFocus, handleBlur],
  );

  return {
    fieldProps,
    fieldState,
    getValue,
    setValue,
    error: form.formState.errors[name] as FieldError | undefined,
    isSubmitted: form.formState.isSubmitted,
  };
}
